import { useCallback, useEffect } from 'react';

import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  useFieldArray,
  useWatch,
} from 'react-hook-form';

import HelperTextContainer from '@components/HelperTextContainer';
import {
  defaultCustomSample,
  defaultSingleSample,
  defaultTrioSample,
} from '@pages/common/Project/Forms/defaults';
import FormSelect from '@components/FormFields/FormSelect';
import SamplesContainer from '@pages/common/Project/Forms/Components/SamplesContainer';
import { type ExperimentTypeSchema, type ProjectSchema } from '@/zod_schemas';
import PipelineComponent from '@/components/Pipeline/PipelineSelect';

interface IExperimentFormProps {
  register: UseFormRegister<ProjectSchema>;
  setValue: UseFormSetValue<ProjectSchema>;
  index: number;
  errors: FieldErrors<ProjectSchema>;
  control: Control<ProjectSchema>;
  changeExperimentType: (type: ExperimentTypeSchema) => void;
}

const ExperimentForm = ({
  index: experimentIndex,
  register,
  errors,
  setValue,
  control,
  changeExperimentType,
}: IExperimentFormProps) => {
  const { append, replace, fields, remove } = useFieldArray({
    control,
    name: `experiments.${experimentIndex}.samples`,
    shouldUnregister: true,
  });

  const experimentType = useWatch({
    control,
    name: `experiments.${experimentIndex}.experiment_type`,
  });

  const handleAddSample = useCallback(() => {
    if (experimentType !== 'custom') {
      changeExperimentType('custom');
    }

    append(defaultCustomSample);
  }, [append, changeExperimentType, experimentType]);

  const experimentErrors = errors.experiments?.[experimentIndex];

  useEffect(() => {
    if (experimentType === 'single') {
      replace([...fields, defaultSingleSample].slice(0, 1));
      return;
    }

    if (experimentType === 'trio' && fields.length < 3) {
      replace([...fields, ...defaultTrioSample].slice(0, 3));
    }
    // Doing this because adding "fields" to the dependency array causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experimentType, replace]);

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={6}>
        <TextField
          id={`experiment-${experimentIndex}-name`}
          label="Experiment name"
          aria-describedby="experiment name field"
          error={Boolean(experimentErrors?.name)}
          helperText={experimentErrors?.name?.message}
          fullWidth
          {...register(`experiments.${experimentIndex}.name`)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth size="small">
          <InputLabel
            id={`experiment-${experimentIndex}-type-label`}
            htmlFor={`experiment-${experimentIndex}-type`}
          >
            Type
          </InputLabel>
          <FormSelect
            selectProps={{
              labelId: `experiment-${experimentIndex}-type-label`,
              id: `experiment-${experimentIndex}-type`,
              label: 'Type',
              'aria-describedby': 'experiment type field',
            }}
            controllerProps={{
              control,
              name: `experiments.${experimentIndex}.experiment_type`,
            }}
          >
            <MenuItem value="single">Single</MenuItem>
            <MenuItem value="trio">Trio</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </FormSelect>
          <HelperTextContainer>
            {experimentErrors?.experiment_type?.message}
          </HelperTextContainer>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          id={`experiment-${experimentIndex}-description`}
          label="Description"
          aria-describedby="experiment description field"
          fullWidth
          {...register(`experiments.${experimentIndex}.description`)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <PipelineComponent
            register={register}
            control={control}
            index={experimentIndex}
            errors={errors}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            mt: 0.5,
            pt: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack spacing={0.25}>
            <Typography variant="subtitle2">Samples and input files</Typography>
            <Typography variant="body2" color="text.secondary">
              Search for a registered sample or create one for this experiment.
            </Typography>
          </Stack>
          <Button variant="contained" onClick={handleAddSample} size="small">
            Add Sample
          </Button>
        </Box>
        <HelperTextContainer>
          <span>
            {experimentErrors?.samples?.root &&
              experimentErrors.samples.root.message}
            {experimentErrors?.samples && experimentErrors.samples.message}
          </span>
        </HelperTextContainer>
      </Grid>
      {fields.length > 0 && (
        <Grid item xs={12}>
          <SamplesContainer
            setValue={setValue}
            control={control}
            experimentIndex={experimentIndex}
            errors={errors}
            register={register}
            fields={fields}
            removeSample={(index: number) => remove(index)}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ExperimentForm;
